import { useCallback, useEffect, useState } from 'react';
import { Activity, AlertTriangle, CheckCircle2, Database, RefreshCw, ShieldCheck, HardDrive, Loader2, ArrowRight, HelpCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-master/components/ui/card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-master/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import api from '@/lib/api';
import type { BackupOverallStatus, BackupStatusResponse } from '@easyflow/shared';

interface PoolMetrics {
  totalCount: number;
  idleCount: number;
  waitingCount: number;
  maxConnections: number;
  utilizationPercent: number;
  healthStatus: 'healthy' | 'warning' | 'critical';
  postgresql?: {
    total_connections: number;
    active: number;
    idle: number;
    idle_in_transaction: number;
    aborted: number;
    max_idle_seconds: number | null;
  };
}

interface ReplicationRow {
  application_name: string;
  state: string;
  sync_state: string;
  /** pg serializza EXTRACT(...) (numeric) come stringa sul wire. */
  write_lag_ms: number | string | null;
  flush_lag_ms: number | string | null;
  replay_lag_ms: number | string | null;
  client_addr: string | null;
}

/** Formatta un lag ms che può arrivare come number o stringa numerica da pg. */
function formatLagMs(v: number | string | null | undefined): string | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'string' ? Number.parseFloat(v) : v;
  if (!Number.isFinite(n)) return null;
  return `${n.toFixed(0)} ms`;
}

interface TopTableRow {
  schemaname: string;
  relname: string;
  size_bytes: string;
  rows_estimate: string;
}

interface RlsGapRow {
  table_name: string;
  has_rls: boolean;
  has_force_rls: boolean;
  policy_count: number;
  tenant_policy_count: number;
}

interface PatroniMember {
  name: string;
  role: string;
  state: string;
  host: string;
  port: number;
  timeline: number | null;
  lag: number | null;
}
interface PatroniClusterSummary {
  scope: string | null;
  members: PatroniMember[];
  fetchedFrom: string | null;
  error?: string;
}
interface TopQueryRow {
  queryid: string;
  query: string;
  calls: string;
  total_exec_ms: number;
  mean_exec_ms: number;
  rows: string;
}
interface AlertRow { severity: 'warning' | 'critical'; code: string; message: string; }

interface ReadPoolServerSide { active: number; idle: number; total: number }
interface ReadPoolMetrics { ourSideAvailable: false; serverSide: ReadPoolServerSide | null }

interface DbMetricsResponse {
  pool: PoolMetrics;
  pools: { write: PoolMetrics; read: ReadPoolMetrics };
  replication: ReplicationRow[];
  topTables: TopTableRow[];
  topQueries: TopQueryRow[];
  topQueriesByMean: TopQueryRow[];
  rlsGap: RlsGapRow[];
  /** Slim server-side: senza r2_objects, stanza.backups cappato agli ultimi 10. */
  pgbackrest: Omit<BackupStatusResponse, 'r2_objects'>;
  patroni: PatroniClusterSummary;
  databaseSize: { bytes: string; pretty: string } | null;
  alerts: AlertRow[];
  timestamp: string;
}

function formatBytes(b: string | number): string {
  const n = typeof b === 'string' ? Number.parseInt(b, 10) : b;
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(1)} ${units[i]}`;
}

interface TracingStatus {
  enabled: boolean;
  endpoint: string | null;
  serviceName: string;
  sampler: string;
  samplerArg: number;
  uiUrl: string | null;
}

interface ObservabilityTabProps {
  onOpenRestore?: () => void;
}

export function ObservabilityTab({ onOpenRestore }: ObservabilityTabProps): JSX.Element {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DbMetricsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tracing, setTracing] = useState<TracingStatus | null>(null);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<DbMetricsResponse>('/master/observability/db/metrics');
      if (res.success && res.data) setData(res.data);
      else setError('Risposta non valida dal server');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'unknown';
      setError(msg);
      toast({ title: 'Errore osservabilità DB', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchTracing = useCallback(async () => {
    try {
      const res = await api.get<TracingStatus>('/master/observability/db/tracing-status');
      if (res.success && res.data) setTracing(res.data);
    } catch {
      setTracing(null);
    }
  }, []);

  useEffect(() => { void fetchMetrics(); void fetchTracing(); }, [fetchMetrics, fetchTracing]);

  const healthBadge = (s: PoolMetrics['healthStatus']): JSX.Element => {
    if (s === 'healthy') return <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/40">healthy</Badge>;
    if (s === 'warning') return <Badge className="bg-amber-500/20 text-amber-700 border-amber-500/40">warning</Badge>;
    return <Badge className="bg-red-500/20 text-red-700 border-red-500/40">critical</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2"><Activity className="h-5 w-5" /> Observability — pool, replica, RLS, backup</h3>
          <p className="text-sm text-muted-foreground">Snapshot completo runtime del cluster Postgres (DB-1 leader + DB-2 replica via HAProxy).</p>
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={() => void fetchMetrics()} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Aggiorna
        </Button>
      </div>

      {error && !loading && (
        <Card className="border-rose-500/40 bg-rose-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-4 w-4" /> Errore lettura metriche
            </CardTitle>
            <CardDescription className="text-rose-600">{error}</CardDescription>
          </CardHeader>
        </Card>
      )}
      {!data && !error && !loading && <p className="text-sm text-muted-foreground/70">Nessun dato.</p>}

      {tracing && (
        tracing.enabled ? (
          <Card className="border-emerald-500/40 bg-emerald-500/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> OpenTelemetry tracing attivo</CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-1">
              <div>service: <code>{tracing.serviceName}</code> · sampler: <code>{tracing.sampler}</code>({tracing.samplerArg})</div>
              <div className="text-muted-foreground">collector: <code>{tracing.endpoint}</code></div>
              {tracing.uiUrl && (
                <div className="pt-2"><a href={tracing.uiUrl} target="_blank" rel="noreferrer" className="underline text-emerald-700">Apri UI traces ↗</a></div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-amber-500/40 bg-amber-500/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Tracing disabilitato</CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              <code>OTEL_EXPORTER_OTLP_ENDPOINT</code> non configurato. Runbook: <code>docs/runbooks/observability-otel.md</code>.
            </CardContent>
          </Card>
        )
      )}

      {data && data.alerts.length > 0 && (
        <Card className="border-red-500/40 bg-red-500/5">
          <CardHeader>
            <CardTitle className="text-red-700 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Alert KPI ({data.alerts.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.alerts.map(a => (
              <div key={a.code} className="flex items-center gap-3 text-sm">
                <Badge className={a.severity === 'critical'
                  ? 'bg-red-500/20 text-red-700 border-red-500/40'
                  : 'bg-amber-500/20 text-amber-700 border-amber-500/40'}>{a.severity}</Badge>
                <code className="text-xs">{a.code}</code>
                <span>{a.message}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          <Card className="border-indigo-200/80 bg-indigo-500/[0.025]">
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <HardDrive className="h-4 w-4 text-indigo-700" /> Evidenza di ripristino
                  </CardTitle>
                  <CardDescription className="mt-1">Lo stato dei backup non prova che un ripristino sia riuscito.</CardDescription>
                </div>
                {onOpenRestore && (
                  <Button variant="outline" size="sm" className="gap-2" onClick={onOpenRestore}>
                    DR &amp; Backup <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="grid gap-3 pt-0 sm:grid-cols-2">
              {(['physical', 'logical'] as const).map((kind) => {
                const verification = data.pgbackrest.restore_gate?.verifications?.[kind] ?? null;
                const status = verification?.status;
                const finished = verification?.finished_at ? Date.parse(verification.finished_at) : NaN;
                const expired = status === 'pass' && (!Number.isFinite(finished) || Date.now() - finished > 30 * 86400_000 || finished > Date.now());
                const collected = data.pgbackrest.collected_at ? Date.parse(data.pgbackrest.collected_at) : NaN;
                const bridgeCurrent = data.pgbackrest.bridge_healthy && Number.isFinite(collected)
                  && Date.now() >= collected && Date.now() - collected <= 45 * 60_000;
                const stateLabel = !bridgeCurrent ? 'Dato stantio' : expired ? 'Scaduto' : status === 'pass' ? 'Superato' : status === 'fail' ? 'Fallito' : status === 'running' ? 'In corso' : status === 'skip' ? 'Saltato' : 'Sconosciuto';
                const StateIcon = bridgeCurrent && !expired && status === 'pass' ? CheckCircle2 : status === 'fail' ? XCircle : HelpCircle;
                const stateStyle = !bridgeCurrent ? 'text-muted-foreground' : expired ? 'text-amber-700' : status === 'pass' ? 'text-emerald-700' : status === 'fail' ? 'text-rose-700' : 'text-muted-foreground';
                const occurredAt = verification?.finished_at ?? verification?.started_at;
                return (
                  <div key={kind} className="flex min-w-0 items-center justify-between gap-3 rounded-md border bg-background/70 px-3 py-2.5">
                    <div className="min-w-0">
                      <div className="text-xs font-medium">Restore {kind === 'physical' ? 'fisico' : 'logico'}</div>
                      <div className="mt-1 truncate text-[11px] text-muted-foreground">
                        {occurredAt && !Number.isNaN(new Date(occurredAt).getTime()) ? new Date(occurredAt).toLocaleString('it-IT') : 'Ultimo esito non noto'}
                        {verification?.duration_seconds !== null && verification?.duration_seconds !== undefined ? ` · ${Math.round(verification.duration_seconds)} s` : ''}
                      </div>
                    </div>
                    <span className={`flex shrink-0 items-center gap-1 text-xs font-medium ${stateStyle}`}><StateIcon className="h-3.5 w-3.5" />{stateLabel}</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2"><Database className="h-4 w-4" /> Pool</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{data.pool.totalCount}/{data.pool.maxConnections}</div>
                <div className="text-xs text-muted-foreground mt-1">idle {data.pool.idleCount} · waiting {data.pool.waitingCount}</div>
                <div className="mt-2">{healthBadge(data.pool.healthStatus)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2"><Activity className="h-4 w-4" /> Replica</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{data.replication.length}</div>
                <div className="text-xs text-muted-foreground mt-1">streaming attive</div>
                {formatLagMs(data.replication[0]?.replay_lag_ms) !== null && (
                  <div className="text-xs mt-1">lag replay: <strong>{formatLagMs(data.replication[0]?.replay_lag_ms)}</strong></div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> RLS gap</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{data.rlsGap.length}</div>
                {data.rlsGap.length === 0
                  ? <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/40 mt-2"><CheckCircle2 className="h-3 w-3 mr-1" /> coverage OK</Badge>
                  : <Badge className="bg-red-500/20 text-red-700 border-red-500/40 mt-2"><AlertTriangle className="h-3 w-3 mr-1" /> tabelle scoperte</Badge>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2"><HardDrive className="h-4 w-4" /> Ultimo backup</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const overallBadge: Record<BackupOverallStatus, string> = {
                    ok: 'bg-emerald-500/20 text-emerald-700 border-emerald-500/40',
                    warning: 'bg-amber-500/20 text-amber-700 border-amber-500/40',
                    critical: 'bg-red-500/20 text-red-700 border-red-500/40',
                    unknown: 'bg-slate-500/20 text-slate-700 border-slate-500/40',
                  };
                  const pb = data.pgbackrest;
                  const lastBackup = pb.stanza?.backups?.[pb.stanza.backups.length - 1] ?? null;
                  const ageH = lastBackup ? Math.round((lastBackup.age_seconds / 3600) * 10) / 10 : null;
                  return (
                    <>
                      <Badge className={overallBadge[pb.overall]}>{pb.overall}</Badge>
                      {!pb.bridge_healthy && (
                        <div className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" /> ponte snapshot degradato
                        </div>
                      )}
                      {lastBackup ? (
                        <>
                          <div className="text-xs text-muted-foreground mt-2">
                            {lastBackup.type} · {new Date(lastBackup.started_at).toLocaleString('it-IT')}
                          </div>
                          {ageH !== null && <div className="text-xs mt-1">età: <strong>{ageH}h</strong></div>}
                        </>
                      ) : <div className="text-sm text-muted-foreground/70 mt-2">nessun backup nello snapshot</div>}
                      {pb.anomalies.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-1">{pb.anomalies.length} anomalie</div>
                      )}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </div>

          {data.databaseSize && (
            <div className="text-sm text-muted-foreground">Dimensione DB: <strong className="text-foreground">{data.databaseSize.pretty}</strong></div>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Patroni cluster — pg_stat_replication + REST :8008/cluster</CardTitle>
              <CardDescription>Scope <code>{data.patroni.scope ?? '—'}</code>, fetched from <code>{data.patroni.fetchedFrom ?? 'n/a'}</code>{data.patroni.error ? ` · ${data.patroni.error}` : ''}.</CardDescription>
            </CardHeader>
            <CardContent>
              {data.patroni.members.length === 0 ? <p className="text-sm text-muted-foreground/70">Patroni REST irraggiungibile.</p> : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Nodo</TableHead><TableHead>Ruolo</TableHead><TableHead>Stato</TableHead>
                    <TableHead>Host</TableHead><TableHead>Timeline</TableHead><TableHead>Lag (B)</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.patroni.members.map(m => (
                      <TableRow key={m.name}>
                        <TableCell className="font-mono text-xs">{m.name}</TableCell>
                        <TableCell>{m.role === 'leader'
                          ? <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/40">leader</Badge>
                          : <Badge variant="outline">{m.role}</Badge>}</TableCell>
                        <TableCell>{m.state}</TableCell>
                        <TableCell className="font-mono text-xs">{m.host}:{m.port}</TableCell>
                        <TableCell>{m.timeline ?? '-'}</TableCell>
                        <TableCell>{m.lag !== null ? m.lag.toLocaleString('it-IT') : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Replica streaming</CardTitle>
              <CardDescription>pg_stat_replication — lag e stato sync</CardDescription>
            </CardHeader>
            <CardContent>
              {data.replication.length === 0 ? <p className="text-sm text-muted-foreground/70">Nessuna replica attiva.</p> : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Replica</TableHead><TableHead>Stato</TableHead><TableHead>Sync</TableHead>
                    <TableHead>Client</TableHead><TableHead>Write lag</TableHead><TableHead>Replay lag</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.replication.map((r) => (
                      <TableRow key={r.application_name}>
                        <TableCell className="font-mono text-xs">{r.application_name}</TableCell>
                        <TableCell>{r.state}</TableCell>
                        <TableCell>{r.sync_state}</TableCell>
                        <TableCell className="font-mono text-xs">{r.client_addr ?? '-'}</TableCell>
                        <TableCell>{formatLagMs(r.write_lag_ms) ?? '-'}</TableCell>
                        <TableCell>{formatLagMs(r.replay_lag_ms) ?? '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>RLS coverage gap</CardTitle>
              <CardDescription>Tabelle con colonna <code>tenant_id</code> ma senza RLS attiva o senza policy che filtri su <code>tenant_id</code>.</CardDescription>
            </CardHeader>
            <CardContent>
              {data.rlsGap.length === 0
                ? <p className="text-sm text-emerald-600">✅ Nessun gap.</p>
                : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Tabella</TableHead><TableHead>RLS enabled</TableHead><TableHead>Force RLS</TableHead>
                      <TableHead>Policy totali</TableHead><TableHead>Policy con tenant_id</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {data.rlsGap.map((g) => (
                        <TableRow key={g.table_name}>
                          <TableCell className="font-mono text-xs">{g.table_name}</TableCell>
                          <TableCell>{g.has_rls ? '✅' : '❌'}</TableCell>
                          <TableCell>{g.has_force_rls ? '✅' : '—'}</TableCell>
                          <TableCell>{g.policy_count}</TableCell>
                          <TableCell>{g.tenant_policy_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Top 20 tabelle per dimensione</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Tabella</TableHead><TableHead>Righe (stima)</TableHead><TableHead>Dimensione</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.topTables.map((t) => (
                    <TableRow key={`${t.schemaname}.${t.relname}`}>
                      <TableCell className="font-mono text-xs">{t.relname}</TableCell>
                      <TableCell>{Number(t.rows_estimate).toLocaleString('it-IT')}</TableCell>
                      <TableCell>{formatBytes(t.size_bytes)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {data.pools.read.serverSide && (
            <Card>
              <CardHeader>
                <CardTitle>Read pool (replica DB-2 via :5433)</CardTitle>
                <CardDescription>Probe pg_stat_activity (server-side) — connessioni applicative `edgvoip-backend-read*`.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div><span className="text-muted-foreground">Active:</span> <strong>{data.pools.read.serverSide.active}</strong></div>
                  <div><span className="text-muted-foreground">Idle:</span> <strong>{data.pools.read.serverSide.idle}</strong></div>
                  <div><span className="text-muted-foreground">Total:</span> <strong>{data.pools.read.serverSide.total}</strong></div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Top 20 query per tempo TOTALE (pg_stat_statements)</CardTitle>
              <CardDescription>Richiede <code>shared_preload_libraries='pg_stat_statements'</code> in Patroni config.</CardDescription>
            </CardHeader>
            <CardContent>
              {data.topQueries.length === 0 ? (
                <p className="text-sm text-muted-foreground/70">pg_stat_statements non abilitato o nessuna statistica disponibile.</p>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Query</TableHead><TableHead>Calls</TableHead>
                    <TableHead>Tot ms</TableHead><TableHead>Avg ms</TableHead><TableHead>Rows</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.topQueries.map(q => (
                      <TableRow key={q.queryid}>
                        <TableCell className="font-mono text-xs max-w-md truncate" title={q.query}>{q.query}</TableCell>
                        <TableCell>{Number(q.calls).toLocaleString('it-IT')}</TableCell>
                        <TableCell>{q.total_exec_ms.toLocaleString('it-IT')}</TableCell>
                        <TableCell>{q.mean_exec_ms.toFixed(2)}</TableCell>
                        <TableCell>{Number(q.rows).toLocaleString('it-IT')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Top 20 query per tempo MEDIO (slow query ranking)</CardTitle>
              <CardDescription>ORDER BY mean_exec_time DESC — utile per identificare query individualmente lente anche se chiamate raramente.</CardDescription>
            </CardHeader>
            <CardContent>
              {data.topQueriesByMean.length === 0 ? (
                <p className="text-sm text-muted-foreground/70">pg_stat_statements non abilitato o nessuna statistica disponibile.</p>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Query</TableHead><TableHead>Calls</TableHead>
                    <TableHead>Avg ms</TableHead><TableHead>Tot ms</TableHead><TableHead>Rows</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.topQueriesByMean.map(q => (
                      <TableRow key={q.queryid}>
                        <TableCell className="font-mono text-xs max-w-md truncate" title={q.query}>{q.query}</TableCell>
                        <TableCell>{Number(q.calls).toLocaleString('it-IT')}</TableCell>
                        <TableCell>{q.mean_exec_ms.toFixed(2)}</TableCell>
                        <TableCell>{q.total_exec_ms.toLocaleString('it-IT')}</TableCell>
                        <TableCell>{Number(q.rows).toLocaleString('it-IT')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
